<script lang="ts">
	import { locale } from '$lib/localization/locale';
	import { message } from '$lib/localization/messages';

	import { redirectToLogin } from '$lib/redirectToLogin';
	import { IS_AQUEDUCT, SITE_NAME } from '$lib/site';
	import AqueductLicense from '$lib/aqueduct/AqueductLicense.svelte';
	import { onMount, onDestroy } from 'svelte';
	import { analytics } from '$lib/analytics';
	import authStore from '$lib/authStore';
	import { page } from '$app/stores';
	import EmailVerificationNotice from '$components/EmailVerificationNotice.svelte';
	import Header from '$components/Header.svelte';
	import PlanInformationClient from '$lib/planInformationClient';
	import type { PlanInformation, UserSettings } from '$lib/diffusionApi';
	import type { User } from 'firebase/auth';
	import Toggle from '$components/Toggle.svelte';
	import { UserSettingsClient } from '$lib/userSettingsClient';
	import FreePlanStarter from '../(iliad)/plan/FreePlanStarter.svelte';
	import SubscribeToPlus from '../(iliad)/plan/SubscribeToPlus.svelte';
	import SubscribeToTeam from '../(iliad)/plan/SubscribeToTeam.svelte';
	import BuyProjectPack from '../(iliad)/plan/BuyProjectPack.svelte';
	import LegacyProfessionalPlan from '../(iliad)/plan/LegacyProfessionalPlan.svelte';
	import ProjectPackCreditsDisplay from '$components/ProjectPackCreditsDisplay.svelte';
	import { needsEmailVerification } from '$lib/emailVerification';
	import {
		disconnectConnectedApp,
		getConnectedApps,
		type ConnectedApp
	} from '$lib/connectedAppsClient';

	let subscriptions: (() => void)[] = [];
	let planInformationClient: PlanInformationClient = new PlanInformationClient('');
	let userSettingsClient: UserSettingsClient = new UserSettingsClient('');
	let planInformation: PlanInformation | undefined = undefined;
	let user: User | null | undefined = undefined;
	let userSettings: UserSettings | undefined = undefined;
	let accountSettingsReady: boolean = false;
	let showEmailVerificationNotice: boolean = false;
	let connectedApps: ConnectedApp[] = [];
	let connectedAppsLoading: boolean = true;
	let connectedAppsError: string = '';
	let disconnectingGrantId: string | null = null;

	const formatConnectionDate = (value: string): string => {
		return new Intl.DateTimeFormat($locale === 'ja' ? 'ja-JP' : undefined, {
			year: 'numeric',
			month: 'short',
			day: 'numeric'
		}).format(new Date(value));
	};

	const connectionActivity = (app: ConnectedApp): string => {
		if (!app.last_used_at) return message($locale, 'account-settings', 'neverUsed');
		return message($locale, 'account-settings', 'lastUsed', {
			value0: formatConnectionDate(app.last_used_at)
		});
	};

	const loadConnectedApps = async (currentUser: User): Promise<void> => {
		connectedAppsLoading = true;
		connectedAppsError = '';
		try {
			connectedApps = await getConnectedApps(await currentUser.getIdToken());
		} catch (error) {
			console.error(error);
			connectedAppsError = message($locale, 'account-settings', 'connectedAppsCouldNotBeLoaded');
		} finally {
			connectedAppsLoading = false;
		}
	};

	const handleDisconnect = async (grantId: string): Promise<void> => {
		if (!user || disconnectingGrantId) return;
		disconnectingGrantId = grantId;
		connectedAppsError = '';
		try {
			await disconnectConnectedApp(await user.getIdToken(), grantId);
			connectedApps = connectedApps.filter((app: ConnectedApp) => app.grant_id !== grantId);
		} catch (error) {
			console.error(error);
			connectedAppsError = message(
				$locale,
				'account-settings',
				'theAppCouldNotBeDisconnectedPleaseTryAgain'
			);
		} finally {
			disconnectingGrantId = null;
		}
	};

	const handleAutoStorageToggle = async () => {
		if (!user || !userSettings) return;
		try {
			await userSettingsClient.patchUserSettings(
				user.uid,
				userSettings.is_automatic_image_storage_on
			);
		} catch (e) {
			console.error(e);
		}
	};

	onMount(async () => {
		localStorage.setItem('redirectUrl', $page.url.pathname + $page.url.search);
		document.body.style.overflowY = 'auto';
		document.body.style.background = '#101016';

		subscriptions.push(
			authStore.subscribe(async (auth) => {
				user = auth.user;
				if (user === undefined) return;
				if (user === null) {
					void redirectToLogin();
					return;
				}
				if (user.email) {
					analytics.identify(user.email, {
						email: user.email,
						name: user.displayName
					});
				}
				showEmailVerificationNotice = needsEmailVerification(user);

				if (IS_AQUEDUCT) {
					await loadConnectedApps(user);
					accountSettingsReady = true;
					return;
				}

				const token = await user.getIdToken();
				planInformationClient.setToken(token ?? '');
				planInformation = await planInformationClient.getPlanInformation();
				if (planInformation) {
					analytics.setPersonProperties({ planName: planInformation.name });
				}

				userSettingsClient.setToken(token ?? '');
				userSettings = await userSettingsClient.getUserSettings();
				accountSettingsReady = planInformation !== undefined && userSettings !== undefined;
			})
		);
	});

	onDestroy(() => {
		for (const _ of subscriptions) _();
	});
</script>

<svelte:head>
	<title>{message($locale, 'account-settings', 'accountSettings')} {SITE_NAME}</title>
</svelte:head>

<div
	class="w-screen h-fit min-h-[100dvh] flex flex-col justify-start items-center
	bg-primary-50 dark:bg-navy-900 text-zinc-900 dark:text-zinc-200 overflow-x-hidden"
>
	{#if accountSettingsReady && user && user.email}
		<div class="w-full h-12 px-2 py-2 flex flex-row justify-start items-center z-10">
			<Header />
			<div class="w-4"></div>
			<div>{message($locale, 'account-settings', 'accountSettings2')}</div>
		</div>

		<div class="h-10 lg:h-24"></div>

		<div class="w-[330px] sm:w-[550px] flex flex-col justify-start items-start">
			{#if showEmailVerificationNotice}
				<div class="h-[8.5rem] w-full sm:h-[5.5rem]">
					<EmailVerificationNotice {user} />
				</div>
				<div class="h-12"></div>
			{/if}

			{#if !IS_AQUEDUCT && userSettings}
				<!-- Iliad-only: image storage is a diffusion-tools concern. -->
				<div class="text-xl">{message($locale, 'account-settings', 'contentPrivacy')}</div>
				<div class="h-6"></div>
				<div class="flex flex-row justify-start items-center">
					<div class="toggle-container">
						<Toggle
							id="svelte-toggle"
							name="svelte-toggle"
							bind:checked={userSettings.is_automatic_image_storage_on}
							ontoggle={handleAutoStorageToggle}
						/>
					</div>
					<div class="w-3"></div>
					<div>
						{message(
							$locale,
							'account-settings',
							'automaticallyStoreNewlyGeneratedImagesInLibrary'
						)}
					</div>
				</div>
				<div class="h-2"></div>
				<div class="text-xs text-zinc-600 dark:text-zinc-400">
					{message($locale, 'account-settings', 'whenToggledOffNoImageDataWillBeStored')}
				</div>
				<div class="h-12"></div>
			{:else}
				<!-- Aqueduct-only: connected apps are OAuth grants to Aqueduct MCP. -->
				<div class="text-xl">{message($locale, 'account-settings', 'connectedApps')}</div>
				<div class="h-2"></div>
				<div class="text-xs text-zinc-600 dark:text-zinc-400">
					{message($locale, 'account-settings', 'appsYouHaveAuthorizedToWorkWithYourAqueduct')}
				</div>
				<div class="h-4"></div>
				{#if connectedAppsLoading}
					<div class="text-sm text-zinc-600 dark:text-zinc-400">
						{message($locale, 'account-settings', 'loadingConnectedApps')}
					</div>
				{:else if connectedApps.length === 0}
					<div class="text-sm text-zinc-600 dark:text-zinc-400">
						{message($locale, 'account-settings', 'noConnectedApps')}
					</div>
				{:else}
					<div class="w-full flex flex-col gap-2">
						{#each connectedApps as app (app.grant_id)}
							<div class="w-full flex flex-row items-center justify-between gap-4 py-1">
								<div class="min-w-0 flex flex-col gap-1">
									<div class="truncate">{app.client_name}</div>
									<div class="text-xs text-zinc-600 dark:text-zinc-400">
										{message($locale, 'account-settings', 'connected')}
										{formatConnectionDate(app.created_at)} · {connectionActivity(app)}
									</div>
								</div>
								<button
									type="button"
									class="w-fit h-12 px-3 shrink-0 flex flex-row justify-center items-center text-center rounded text-sm font-semibold
								text-zinc-900 bg-primary-50 border border-primary-200 hover:brightness-90
								dark:text-zinc-200 dark:hover:bg-cyan-300 dark:border-none dark:hover:bg-opacity-10 dark:bg-zinc-800
								disabled:cursor-wait disabled:opacity-60"
									disabled={disconnectingGrantId !== null}
									on:click={() => handleDisconnect(app.grant_id)}
								>
									{disconnectingGrantId === app.grant_id
										? message($locale, 'account-settings', 'disconnecting')
										: message($locale, 'account-settings', 'disconnect')}
								</button>
							</div>
						{/each}
					</div>
				{/if}
				{#if connectedAppsError}
					<div class="mt-3 text-xs text-red-500">{connectedAppsError}</div>
				{/if}

				<div class="h-12"></div>
			{/if}
			{#if IS_AQUEDUCT}
				{#key user.uid}
					<AqueductLicense {user} />
				{/key}
			{:else}
				<div class="text-xl">{message($locale, 'account-settings', 'yourPlan')}</div>
				<div class="h-6"></div>
				{#if planInformation}
					{#if planInformation.name === 'Starter'}
						{#if planInformation.credits === null}
							<FreePlanStarter showCallToAction={false} isUsersPlan={true} />
						{:else}
							<BuyProjectPack
								email={user ? user.email ?? '' : ''}
								showCallToAction={false}
								isUsersPlan={true}
							/>
						{/if}
					{:else if planInformation.name === 'Plus'}
						<SubscribeToPlus
							{planInformation}
							email={user.email}
							showCallToAction={true}
							isUsersPlan={true}
							{user}
						/>
					{:else if planInformation.name === 'Team'}
						<SubscribeToTeam showCallToAction={true} isUsersPlan={true} />
					{:else if planInformation.name === 'Professional'}
						<LegacyProfessionalPlan email={user.email} />
					{/if}

					{#if planInformation.name === 'Starter' && planInformation.credits !== null}
						<div class="h-6"></div>
						<ProjectPackCreditsDisplay credits={planInformation.credits} />
					{/if}
				{/if}
			{/if}
		</div>

		<div class="h-12"></div>
	{/if}
</div>
